// ============================================================
// LUCAT CONNECT FAMILLE — Source unique des données de démonstration
// familyDemoData.ts
// ============================================================

// ─── Image constants ─────────────────────────────────────────────────────────

export const ANDRE_PHOTO_URL =
  'https://img.rocket.new/generatedImages/rocket_gen_img_1bc93b3ef-1767526138775.png';
export const MARIE_AVATAR_URL =
  'https://img.rocket.new/generatedImages/rocket_gen_img_1645d3016-1773176995009.png';
export const JEAN_AVATAR_URL =
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face';
export const SOPHIE_AVATAR_URL =
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=face';
export const PAUL_AVATAR_URL =
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face';

export const DEMO_PHOTO_SUNSET_URL =
  'https://img.rocket.new/generatedImages/rocket_gen_img_155d110df-1778320201227.png';

// ─── Demo date ────────────────────────────────────────────────────────────────

export const DEMO_DATE = '2026-07-27';

// ─── Canonical types ──────────────────────────────────────────────────────────

export type NotificationStatus = 'unread' | 'read' | 'deferred' | 'handled';
export type NotificationType =
  | 'callback_request' |'missed_call' |'new_message' |'new_voice_message' |'new_photo' |'connection_lost' |'connection_restored' |'upcoming_event';

export interface Notification {
  id: string;
  seniorId: string;
  recipientFamilyUserId: string;
  type: NotificationType;
  title: string;
  description: string;
  createdAt: string;
  status: NotificationStatus;
  readAt?: string;
  handledAt?: string;
  deferredAt?: string;
  sourceEntityId?: string;
  actionRoute?: string;
}

export type CallStatus = 'ringing' | 'answered' | 'refused' | 'cancelled' | 'completed' | 'missed';
export type CallType = 'video' | 'audio';
export type CallDirection = 'incoming' | 'outgoing';

export interface CallRecord {
  id: string;
  seniorId: string;
  familyUserId: string;
  direction: CallDirection;
  type: CallType;
  sourceNotificationId: string | null;
  startedAt: string;
  connectedAt?: string;
  endedAt?: string;
  durationSeconds?: number;
  status: CallStatus;
}

export type MessageType =
  | 'text' |'photo' |'voice' |'system' |'callback_request' |'call_summary';

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  recipientId: string;
  type: MessageType;
  text?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
  createdAt: string;
  readAt?: string;
  status: 'sent' | 'delivered' | 'read' | 'listened' | 'handled';
  // Legacy compat fields used by MessagesClient
  contactId?: string;
  seniorId?: string;
  content?: string;
  sentAt?: string;
  readStatus?: string;
  direction?: 'incoming' | 'outgoing';
  callType?: CallType;
  callStatus?: 'ended' | 'missed' | 'declined' | 'cancelled';
  notifId?: string;
}

export interface Conversation {
  id: string;
  seniorId: string;
  familyUserId: string;
  messageIds: string[];
  lastMessageAt: string;
  unreadCount: number;
}

export interface AgendaEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  type: 'visit' | 'call' | 'family' | 'activity' | 'reminder';
  concernedPersonId: string | null;
  note?: string;
  reminderEnabled: boolean;
  status: 'upcoming' | 'done' | 'cancelled';
  createdBy: string;
  // Legacy compat
  label?: string;
  contactId?: string | null;
  seniorId?: string;
  color?: string;
  isoDate?: string;
  reminder?: boolean;
}

export type ActivityCategory =
  | 'call' |'message' |'photo' |'radio' |'agenda' |'quiz' |'connection' |'callback';

export type ActivityStatus =
  | 'new' |'seen' |'pending' |'deferred' |'calling' |'handled' |'failed' |'informational';

export interface ActivityRecord {
  id: string;
  seniorId: string;
  actorId?: string;
  type: string;
  title: string;
  description?: string;
  createdAt: string;
  relatedEntityId?: string;
  status: ActivityStatus;
  // Legacy compat
  category?: ActivityCategory;
  action?: string;
  label?: string;
  contactId?: string;
  durationSeconds?: number;
  metadata?: Record<string, string | number | boolean>;
}

export interface FamilyContactPermission {
  familyContactId: string;
  canCall: boolean;
  canReceiveCallbackRequests: boolean;
  canSendMessages: boolean;
  canSendPhotos: boolean;
  canManageAgenda: boolean;
  canReceiveConnectionAlerts: boolean;
  canViewActivities: boolean;
  canManageSeniorSettings: boolean;
}

export interface FamilyContact {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
  email: string;
  avatarUrl: string;
  status: 'admin' | 'authorized' | 'secondary' | 'pending_invite';
  isActive: boolean;
  isAdmin: boolean;
  permissions: {
    canCall: boolean;
    canReceiveCallbacks: boolean;
    canSendMessages: boolean;
    canSendPhotos: boolean;
    canEditAgenda: boolean;
    canReceiveConnectionAlerts: boolean;
    canViewActivities: boolean;
    canEditSettings: boolean;
  };
}

export interface SeniorProfile {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
  connectionStatus: string;
  socialStatus: string;
  lastSeenAt: string;
  lastCallAt?: string;
  lastCallDuration?: string;
  device: string;
  favoriteRadio: string;
}

export interface CurrentFamilyUser {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  relation: string;
  avatarUrl: string;
}

// ─── Full demo state ──────────────────────────────────────────────────────────

export interface FamilyDemoState {
  schemaVersion: number;
  seniorProfile: SeniorProfile;
  currentFamilyUser: CurrentFamilyUser;
  notifications: Notification[];
  callRecords: CallRecord[];
  messages: Message[];
  conversations: Conversation[];
  agendaEvents: AgendaEvent[];
  activities: ActivityRecord[];
  familyContacts: FamilyContact[];
  lastResetAt?: string;
}

// ─── Initial state ────────────────────────────────────────────────────────────

export function buildInitialDemoState(): FamilyDemoState {
  return {
    schemaVersion: 1,

    seniorProfile: {
      id: 'andre',
      firstName: 'André',
      lastName: 'Beaumont',
      photoUrl: ANDRE_PHOTO_URL,
      connectionStatus: 'Connecté il y a quelques minutes',
      socialStatus: "Tout semble normal aujourd'hui",
      lastSeenAt: `${DEMO_DATE}T17:55:00-04:00`,
      lastCallAt: `${DEMO_DATE}T18:20:00-04:00`,
      lastCallDuration: '12 minutes',
      device: 'Tablette LUCAT CONNECT',
      favoriteRadio: 'RCI',
    },

    currentFamilyUser: {
      id: 'marie',
      firstName: 'Marie',
      lastName: 'Beaumont',
      role: 'administratrice',
      relation: 'Fille d\'André',
      avatarUrl: MARIE_AVATAR_URL,
    },

    notifications: [
      {
        id: 'notif-callback-001',
        seniorId: 'andre',
        recipientFamilyUserId: 'marie',
        type: 'callback_request',
        title: 'André souhaite être appelé',
        description: "Demande envoyée aujourd'hui à 14h20",
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        status: 'unread',
        sourceEntityId: 'msg-notif-callback',
        actionRoute: '/outgoing-call-screen',
      },
      {
        id: 'notif-missed-001',
        seniorId: 'andre',
        recipientFamilyUserId: 'marie',
        type: 'missed_call',
        title: "Appel manqué d'André",
        description: 'Hier à 09h15 — Appel vidéo',
        createdAt: `${DEMO_DATE}T13:15:00-04:00`,
        status: 'read',
        readAt: `${DEMO_DATE}T14:00:00-04:00`,
        actionRoute: '/outgoing-call-screen',
      },
      {
        id: 'notif-photo-001',
        seniorId: 'andre',
        recipientFamilyUserId: 'marie',
        type: 'new_photo',
        title: 'André a vu votre photo',
        description: "Aujourd'hui à 14h32 — Photo du coucher de soleil",
        createdAt: `${DEMO_DATE}T18:32:00-04:00`,
        status: 'handled',
        readAt: `${DEMO_DATE}T18:33:00-04:00`,
        handledAt: `${DEMO_DATE}T18:33:00-04:00`,
        sourceEntityId: 'msg-001',
        actionRoute: '/messages',
      },
      {
        id: 'notif-tablet-001',
        seniorId: 'andre',
        recipientFamilyUserId: 'marie',
        type: 'connection_lost',
        title: "La tablette d'André était hors ligne",
        description: 'Avant-hier soir — Connexion rétablie',
        createdAt: '2026-07-26T22:00:00-04:00',
        status: 'handled',
        readAt: '2026-07-27T08:00:00-04:00',
        handledAt: '2026-07-27T08:00:00-04:00',
      },
    ],

    callRecords: [
      {
        id: 'call-001',
        seniorId: 'andre',
        familyUserId: 'marie',
        direction: 'outgoing',
        type: 'video',
        sourceNotificationId: null,
        startedAt: `${DEMO_DATE}T18:20:00-04:00`,
        connectedAt: `${DEMO_DATE}T18:20:30-04:00`,
        endedAt: `${DEMO_DATE}T18:32:00-04:00`,
        durationSeconds: 720,
        status: 'completed',
      },
      {
        id: 'call-002',
        seniorId: 'andre',
        familyUserId: 'marie',
        direction: 'incoming',
        type: 'video',
        sourceNotificationId: null,
        startedAt: `${DEMO_DATE}T13:15:00-04:00`,
        status: 'missed',
      },
      {
        id: 'call-003',
        seniorId: 'andre',
        familyUserId: 'marie',
        direction: 'outgoing',
        type: 'audio',
        sourceNotificationId: null,
        startedAt: '2026-07-26T11:30:00-04:00',
        connectedAt: '2026-07-26T11:30:30-04:00',
        endedAt: '2026-07-26T11:38:00-04:00',
        durationSeconds: 480,
        status: 'completed',
      },
    ],

    messages: [
      {
        id: 'msg-call-001',
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'call_summary',
        text: 'Appel vidéo — 12 minutes',
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: 'Appel vidéo — 12 minutes',
        sentAt: `${DEMO_DATE}T18:20:00-04:00`,
        readStatus: 'seen',
        direction: 'outgoing',
        callType: 'video',
        callStatus: 'ended',
        durationSeconds: 720,
      },
      {
        id: 'msg-003',
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'voice',
        text: 'Message vocal de Jean — 0:42',
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        status: 'listened',
        durationSeconds: 42,
        // Legacy compat
        contactId: 'jean',
        seniorId: 'andre',
        content: 'Message vocal de Jean — 0:42',
        sentAt: `${DEMO_DATE}T18:20:00-04:00`,
        readStatus: 'listened',
        direction: 'outgoing',
      },
      {
        id: 'msg-notif-listened',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'system',
        text: 'André a écouté votre message vocal',
        createdAt: `${DEMO_DATE}T18:25:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: 'André a écouté votre message vocal',
        sentAt: `${DEMO_DATE}T18:25:00-04:00`,
        readStatus: 'seen',
        direction: 'incoming',
      },
      {
        id: 'msg-call-missed',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'call_summary',
        text: "Appel manqué d'André",
        createdAt: `${DEMO_DATE}T13:15:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: "Appel manqué d'André",
        sentAt: `${DEMO_DATE}T13:15:00-04:00`,
        readStatus: 'seen',
        direction: 'incoming',
        callType: 'video',
        callStatus: 'missed',
      },
      {
        id: 'msg-notif-callback',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'callback_request',
        text: 'André souhaite être appelé',
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        status: 'delivered',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: 'André souhaite être appelé',
        sentAt: `${DEMO_DATE}T18:20:00-04:00`,
        readStatus: 'sent',
        direction: 'incoming',
        notifId: 'notif-callback-001',
      },
      {
        id: 'msg-001',
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'photo',
        text: 'Regardez ce coucher de soleil depuis la terrasse ! 🌅',
        mediaUrl: DEMO_PHOTO_SUNSET_URL,
        thumbnailUrl: DEMO_PHOTO_SUNSET_URL,
        createdAt: `${DEMO_DATE}T18:32:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: 'Regardez ce coucher de soleil depuis la terrasse ! 🌅',
        sentAt: `${DEMO_DATE}T18:32:00-04:00`,
        readStatus: 'seen',
        direction: 'outgoing',
      },
      {
        id: 'msg-notif-photo-seen',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'system',
        text: 'André a vu votre photo',
        createdAt: `${DEMO_DATE}T18:35:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: 'André a vu votre photo',
        sentAt: `${DEMO_DATE}T18:35:00-04:00`,
        readStatus: 'seen',
        direction: 'incoming',
      },
      {
        id: 'msg-002',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'text',
        text: "Merci ma chérie, c'est magnifique ! Je t'attends pour la visite de cet après-midi.",
        createdAt: `${DEMO_DATE}T18:36:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: "Merci ma chérie, c'est magnifique ! Je t'attends pour la visite de cet après-midi.",
        sentAt: `${DEMO_DATE}T18:36:00-04:00`,
        readStatus: 'seen',
        direction: 'incoming',
      },
      {
        id: 'msg-004',
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'text',
        text: "Papa, j'arrive vers 15h00 comme prévu. Tu as besoin de quelque chose ?",
        createdAt: `${DEMO_DATE}T16:10:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: "Papa, j'arrive vers 15h00 comme prévu. Tu as besoin de quelque chose ?",
        sentAt: `${DEMO_DATE}T16:10:00-04:00`,
        readStatus: 'seen',
        direction: 'outgoing',
      },
      {
        id: 'msg-005',
        conversationId: 'conv-andre-marie',
        senderId: 'andre',
        recipientId: 'marie',
        type: 'text',
        text: 'Rien de spécial, juste te voir ! 😊',
        createdAt: `${DEMO_DATE}T16:18:00-04:00`,
        status: 'read',
        // Legacy compat
        contactId: 'andre',
        seniorId: 'andre',
        content: 'Rien de spécial, juste te voir ! 😊',
        sentAt: `${DEMO_DATE}T16:18:00-04:00`,
        readStatus: 'seen',
        direction: 'incoming',
      },
    ],

    conversations: [
      {
        id: 'conv-andre-marie',
        seniorId: 'andre',
        familyUserId: 'marie',
        messageIds: [
          'msg-call-001',
          'msg-003',
          'msg-notif-listened',
          'msg-call-missed',
          'msg-notif-callback',
          'msg-001',
          'msg-notif-photo-seen',
          'msg-002',
          'msg-004',
          'msg-005',
        ],
        lastMessageAt: `${DEMO_DATE}T18:36:00-04:00`,
        unreadCount: 1,
      },
    ],

    agendaEvents: [
      {
        id: 'evt-001',
        title: 'Visite de Marie',
        date: "Aujourd'hui",
        time: '15h00',
        type: 'visit',
        concernedPersonId: 'marie',
        reminderEnabled: true,
        status: 'upcoming',
        createdBy: 'marie',
        // Legacy compat
        label: 'Visite de Marie',
        contactId: 'marie',
        seniorId: 'andre',
        color: 'lucat-green',
        isoDate: `${DEMO_DATE}`,
        reminder: true,
      },
      {
        id: 'evt-002',
        title: 'Appel avec Jean',
        date: 'Demain',
        time: '10h00',
        type: 'call',
        concernedPersonId: 'jean',
        reminderEnabled: false,
        status: 'upcoming',
        createdBy: 'marie',
        // Legacy compat
        label: 'Appel avec Jean',
        contactId: 'jean',
        seniorId: 'andre',
        color: 'lucat-blue',
        isoDate: '2026-07-28',
      },
      {
        id: 'evt-003',
        title: 'Goûter en famille',
        date: 'Samedi',
        time: '16h00',
        type: 'family',
        concernedPersonId: null,
        reminderEnabled: false,
        status: 'upcoming',
        createdBy: 'marie',
        // Legacy compat
        label: 'Goûter en famille',
        contactId: null,
        seniorId: 'andre',
        color: 'lucat-purple',
        isoDate: '2026-08-01',
      },
    ],

    activities: [
      {
        id: 'ra-001',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'photo',
        title: 'André a vu votre photo',
        description: 'Photo du coucher de soleil depuis la terrasse',
        createdAt: `${DEMO_DATE}T18:32:00-04:00`,
        relatedEntityId: 'msg-001',
        status: 'seen',
        // Legacy compat
        category: 'photo',
        action: 'photo_seen',
        label: 'André a vu votre photo',
        contactId: 'marie',
        metadata: { mediaId: 'msg-001' },
      },
      {
        id: 'ra-002',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'message',
        title: 'André a écouté votre message vocal',
        description: 'Message vocal de 42 secondes',
        createdAt: `${DEMO_DATE}T18:25:00-04:00`,
        relatedEntityId: 'msg-003',
        status: 'seen',
        durationSeconds: 42,
        // Legacy compat
        category: 'message',
        action: 'voice_listened',
        label: 'André a écouté votre message vocal',
        contactId: 'marie',
        metadata: { messageId: 'msg-003' },
      },
      {
        id: 'ra-003',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'call',
        title: 'Appel vidéo terminé avec André',
        description: 'Appel vidéo sortant',
        createdAt: `${DEMO_DATE}T16:18:00-04:00`,
        relatedEntityId: 'call-today-001',
        status: 'informational',
        durationSeconds: 18,
        // Legacy compat
        category: 'call',
        action: 'call_ended',
        label: 'Appel vidéo terminé avec André',
        contactId: 'marie',
        metadata: { callId: 'call-today-001', callType: 'video', direction: 'outgoing' },
      },
      {
        id: 'ra-004',
        seniorId: 'andre',
        type: 'radio',
        title: 'André a écouté RCI',
        description: 'Radio des Caraïbes Internationales',
        createdAt: `${DEMO_DATE}T15:15:00-04:00`,
        status: 'informational',
        // Legacy compat
        category: 'radio',
        action: 'radio_listened',
        label: 'André a écouté RCI',
        metadata: { station: 'RCI' },
      },
      {
        id: 'ra-005',
        seniorId: 'andre',
        type: 'agenda',
        title: "André a consulté sa journée",
        description: "Consultation de l'agenda du jour",
        createdAt: `${DEMO_DATE}T13:30:00-04:00`,
        relatedEntityId: 'evt-001',
        status: 'informational',
        // Legacy compat
        category: 'agenda',
        action: 'agenda_viewed',
        label: "André a consulté sa journée",
        metadata: { eventId: 'evt-001' },
      },
      {
        id: 'ra-006',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'callback',
        title: 'André a demandé à être rappelé',
        description: 'Demande de rappel envoyée',
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        relatedEntityId: 'notif-callback-001',
        status: 'new',
        // Legacy compat
        category: 'callback',
        action: 'callback_requested',
        label: 'André a demandé à être rappelé',
        contactId: 'marie',
        metadata: { notificationId: 'notif-callback-001' },
      },
      {
        id: 'ra-007',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'call',
        title: 'Appel vidéo terminé avec André — 12 minutes',
        description: 'Appel vidéo sortant',
        createdAt: `${DEMO_DATE}T18:20:00-04:00`,
        relatedEntityId: 'call-001',
        status: 'informational',
        durationSeconds: 720,
        // Legacy compat
        category: 'call',
        action: 'call_ended',
        label: 'Appel vidéo terminé avec André — 12 minutes',
        contactId: 'marie',
        metadata: { callId: 'call-001', callType: 'video', direction: 'outgoing' },
      },
      {
        id: 'ra-008',
        seniorId: 'andre',
        type: 'quiz',
        title: 'Quiz Martinique terminé à 65 %',
        description: 'Quiz culturel sur la Martinique',
        createdAt: `${DEMO_DATE}T17:30:00-04:00`,
        status: 'informational',
        // Legacy compat
        category: 'quiz',
        action: 'quiz_completed',
        label: 'Quiz Martinique terminé à 65 %',
        metadata: { quizName: 'Quiz Martinique', score: 65 },
      },
      {
        id: 'ra-009',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'callback',
        title: 'Demande de rappel traitée',
        description: "André avait demandé à être rappelé — Appel effectué",
        createdAt: `${DEMO_DATE}T16:20:00-04:00`,
        relatedEntityId: 'notif-callback-old',
        status: 'handled',
        // Legacy compat
        category: 'callback',
        action: 'callback_handled',
        label: 'Demande de rappel traitée',
        contactId: 'marie',
        metadata: { notificationId: 'notif-callback-old' },
      },
      {
        id: 'ra-010',
        seniorId: 'andre',
        type: 'connection',
        title: "La tablette d'André s'est reconnectée",
        description: "Connexion rétablie après une interruption",
        createdAt: `${DEMO_DATE}T12:45:00-04:00`,
        status: 'informational',
        // Legacy compat
        category: 'connection',
        action: 'tablet_reconnected',
        label: "La tablette d'André s'est reconnectée",
        metadata: {},
      },
      {
        id: 'ra-011',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'call',
        title: 'Appel audio terminé avec André — 8 minutes',
        description: 'Appel audio sortant',
        createdAt: '2026-07-26T11:30:00-04:00',
        relatedEntityId: 'call-003',
        status: 'informational',
        durationSeconds: 480,
        // Legacy compat
        category: 'call',
        action: 'call_ended',
        label: 'Appel audio terminé avec André — 8 minutes',
        contactId: 'marie',
        metadata: { callId: 'call-003', callType: 'audio', direction: 'outgoing' },
      },
      {
        id: 'ra-012',
        seniorId: 'andre',
        type: 'connection',
        title: "La tablette d'André était hors ligne",
        description: 'Interruption de connexion détectée le soir',
        createdAt: '2026-07-26T22:00:00-04:00',
        relatedEntityId: 'notif-tablet-001',
        status: 'handled',
        // Legacy compat
        category: 'connection',
        action: 'tablet_offline',
        label: "La tablette d'André était hors ligne",
        metadata: { notificationId: 'notif-tablet-001' },
      },
      {
        id: 'ra-013',
        seniorId: 'andre',
        actorId: 'marie',
        type: 'photo',
        title: 'Vous avez envoyé une photo à André',
        description: 'Photo du jardin en fleurs',
        createdAt: '2026-07-26T14:10:00-04:00',
        status: 'informational',
        // Legacy compat
        category: 'photo',
        action: 'photo_sent',
        label: 'Vous avez envoyé une photo à André',
        contactId: 'marie',
        metadata: { mediaId: 'photo-old-001' },
      },
      {
        id: 'ra-014',
        seniorId: 'andre',
        type: 'radio',
        title: 'André a écouté France Inter',
        description: "Écoute de la matinale",
        createdAt: '2026-07-26T08:00:00-04:00',
        status: 'informational',
        // Legacy compat
        category: 'radio',
        action: 'radio_listened',
        label: 'André a écouté France Inter',
        metadata: { station: 'France Inter' },
      },
    ],

    familyContacts: [
      {
        id: 'fc-marie',
        firstName: 'Marie',
        lastName: 'Beaumont',
        relationship: 'Fille',
        phone: '06 00 00 00 01',
        email: 'marie@lucat-famille.fr',
        avatarUrl: MARIE_AVATAR_URL,
        status: 'admin',
        isActive: true,
        isAdmin: true,
        permissions: {
          canCall: true,
          canReceiveCallbacks: true,
          canSendMessages: true,
          canSendPhotos: true,
          canEditAgenda: true,
          canReceiveConnectionAlerts: true,
          canViewActivities: true,
          canEditSettings: true,
        },
      },
      {
        id: 'fc-jean',
        firstName: 'Jean',
        lastName: 'Beaumont',
        relationship: 'Fils',
        phone: '06 00 00 00 02',
        email: 'jean.beaumont@email.fr',
        avatarUrl: JEAN_AVATAR_URL,
        status: 'authorized',
        isActive: true,
        isAdmin: false,
        permissions: {
          canCall: true,
          canReceiveCallbacks: true,
          canSendMessages: true,
          canSendPhotos: true,
          canEditAgenda: false,
          canReceiveConnectionAlerts: false,
          canViewActivities: true,
          canEditSettings: false,
        },
      },
      {
        id: 'fc-sophie',
        firstName: 'Sophie',
        lastName: 'Beaumont',
        relationship: 'Petite-fille',
        phone: '06 00 00 00 03',
        email: 'sophie.beaumont@email.fr',
        avatarUrl: SOPHIE_AVATAR_URL,
        status: 'authorized',
        isActive: true,
        isAdmin: false,
        permissions: {
          canCall: true,
          canReceiveCallbacks: false,
          canSendMessages: true,
          canSendPhotos: true,
          canEditAgenda: false,
          canReceiveConnectionAlerts: false,
          canViewActivities: false,
          canEditSettings: false,
        },
      },
      {
        id: 'fc-paul',
        firstName: 'Paul',
        lastName: 'Beaumont',
        relationship: 'Frère',
        phone: '06 00 00 00 04',
        email: 'paul.beaumont@email.fr',
        avatarUrl: PAUL_AVATAR_URL,
        status: 'secondary',
        isActive: true,
        isAdmin: false,
        permissions: {
          canCall: true,
          canReceiveCallbacks: false,
          canSendMessages: false,
          canSendPhotos: false,
          canEditAgenda: false,
          canReceiveConnectionAlerts: false,
          canViewActivities: false,
          canEditSettings: false,
        },
      },
    ],
  };
}

// ─── Utility helpers ──────────────────────────────────────────────────────────

export function formatDurationLabel(secs: number): string {
  if (secs < 60) return `${secs} seconde${secs > 1 ? 's' : ''}`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} minute${m > 1 ? 's' : ''}`;
}

export function formatLastCallLabel(isoDate: string): string {
  try {
    const callDate = new Date(isoDate);
    const now = new Date();
    const h = callDate.getHours().toString().padStart(2, '0');
    const m = callDate.getMinutes().toString().padStart(2, '0');
    if (callDate.toDateString() === now.toDateString()) {
      return `aujourd'hui à ${h}h${m}`;
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (callDate.toDateString() === yesterday.toDateString()) {
      return `hier à ${h}h${m}`;
    }
    return (
      callDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) +
      ` à ${h}h${m}`
    );
  } catch {
    return 'date inconnue';
  }
}
